import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IInventoryRequestDeclineRequest,
  IInventoryRequestPostRequest,
  IInventoryRequestResponse,
} from 'types/api/inventoryRequestApi.interface'
import { IAddInventoryRequestFormState, IInventoryRequestsSlice } from 'types/inventoryRequests.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, getParams, handleErrors } from 'utils/helpers'

const initialState: IInventoryRequestsSlice = {
  isLoading: false,
  requests: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
}

export const getInventoryRequests = createAsyncThunk(
  'inventoryRequests/getInventoryRequests',
  async (params: any, thunkAPI) => {
    try {
      const { listType, ...apiParams } = params
      const formattedParams = formatParams(apiParams)
      const endpoint = listType === 'all' ? 'inventory/request' : 'inventory/request/my'
      const resp: AxiosResponse<IPaginatedResponse<IInventoryRequestResponse>> = await baseUrl.get(
        endpoint,
        { params: formattedParams },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addInventoryRequest = createAsyncThunk(
  'inventoryRequests/addInventoryRequest',
  async (data: IAddInventoryRequestFormState, thunkAPI) => {
    try {
      const request: IInventoryRequestPostRequest = {
        departmentId: data.department?.value,
        costCenterId: data.costCenter?.value || null,
        notes: data.notes || null,
        lines: data.lines.map((line) => ({
          itemId: line.item?.value,
          quantity: line.quantity,
        })),
      }
      const resp: AxiosResponse<IInventoryRequestResponse> = await baseUrl.post(
        'inventory/request',
        request,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const approveInventoryRequest = createAsyncThunk(
  'inventoryRequests/approveInventoryRequest',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`inventory/request/${id}/approve`)
      await thunkAPI.dispatch(getInventoryRequests(getParams({ location, limit: 30 })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const declineInventoryRequest = createAsyncThunk(
  'inventoryRequests/declineInventoryRequest',
  async ({ id, declineReason }: { id: number; declineReason: string }, thunkAPI) => {
    try {
      const body: IInventoryRequestDeclineRequest = { declineReason }
      await baseUrl.post(`inventory/request/${id}/decline`, body)
      await thunkAPI.dispatch(getInventoryRequests(getParams({ location, limit: 30 })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const routeInventoryRequestToProcurement = createAsyncThunk(
  'inventoryRequests/routeInventoryRequestToProcurement',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`inventory/request/${id}/procurement`)
      await thunkAPI.dispatch(getInventoryRequests(getParams({ location, limit: 30 })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const issueInventoryRequest = createAsyncThunk(
  'inventoryRequests/issueInventoryRequest',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`inventory/request/${id}/issue`)
      await thunkAPI.dispatch(getInventoryRequests(getParams({ location, limit: 30 })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const inventoryRequestsSlice = createSlice({
  name: 'inventoryRequests',
  initialState,
  reducers: {
    clearInventoryRequests: (state) => {
      state.requests.data = []
      state.requests.totalCount = 0
      state.requests.totalPages = 0
    },
  },
  extraReducers: {
    [getInventoryRequests.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryRequests.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryRequestResponse> },
    ) => {
      state.isLoading = false
      state.requests = payload
    },
    [getInventoryRequests.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addInventoryRequest.pending.type]: (state) => {
      state.isLoading = true
    },
    [addInventoryRequest.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Inventory request created')
    },
    [addInventoryRequest.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [approveInventoryRequest.pending.type]: (state) => {
      state.isLoading = true
    },
    [approveInventoryRequest.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Inventory request approved successfully.')
    },
    [approveInventoryRequest.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [declineInventoryRequest.pending.type]: (state) => {
      state.isLoading = true
    },
    [declineInventoryRequest.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Inventory request declined successfully.')
    },
    [declineInventoryRequest.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [routeInventoryRequestToProcurement.pending.type]: (state) => {
      state.isLoading = true
    },
    [routeInventoryRequestToProcurement.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Procurement purchase request created successfully.')
    },
    [routeInventoryRequestToProcurement.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [issueInventoryRequest.pending.type]: (state) => {
      state.isLoading = true
    },
    [issueInventoryRequest.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Inventory request issued successfully.')
    },
    [issueInventoryRequest.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearInventoryRequests } = inventoryRequestsSlice.actions

export default inventoryRequestsSlice.reducer
